import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  CloseButton,
  Dialog,
  Field,
  Flex,
  Input,
  InputGroup,
  NativeSelect,
  Portal,
  Text,
} from '@chakra-ui/react';
import type { FormErrors } from '@customTypes/errors';
import { capitalizeFirstLetter } from '@utils/formats';
import { toast } from 'react-toastify';
import { datadogRum } from '@datadog/browser-rum';
import api from '@services/api';
import ErrorLabel from '@components/ErrorLabel';
import Loading from '@components/Loading';
import SaleFunnelSelect from '@components/SaleFunnelSelect';
import useGetSalesFunnelsList from '@hooks/useGetSalesFunnelsList';
import CustomDialogBackdrop from '@components/CustomDialogBackdrop';

import type { OptionType } from '../types';
import useGetBehaviours from '../useGetBehaviours';

interface Props {
  handleClose: () => void;
  afterSubmit: () => void;
  option?: OptionType,
};

const UpsertDialog = ({ handleClose, afterSubmit, option }: Props) => {
  const {
    getSalesFunnels,
    salesFunnels,
  } = useGetSalesFunnelsList();

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<FormErrors | null>(null);
  const [selectedBehaviour, setSelectedBehaviour] = useState(option?.behaviour || undefined);
  const [askNegotiatedAmount, setAskNegotiatedAmount] = useState(option?.ask_negotiated_amount || false);
  const [isVisibleInASpecificFunnel, setIsVisibleInASpecificFunnel] = useState(option?.is_visible_in_a_specific_funnel || false);
  const [selectedVisibleOnFunnel, setSelectedVisibleOnFunnel] = useState(option?.visible_on_funnel || null);
  const [shouldMoveCustomerToNextFunnel, setShouldMoveCustomerToNextFunnel] = useState(option?.should_move_customer_to_next_funnel || false);
  const [selectedNextFunnel, setSelectedNextFunnel] = useState(option?.next_funnel || null);

  const isEditing = !!option;

  const { behaviours, loading: loadingBehaviours } = useGetBehaviours();

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const formData = new FormData(event.currentTarget);
    const data = Object.fromEntries(formData.entries());

    if (!selectedBehaviour) {
      setErrors({ behaviour: ['É necessário selecionar um comportamento.'] });
      return;
    }

    if (isVisibleInASpecificFunnel && !selectedVisibleOnFunnel) {
      setErrors({ visible_on_funnel: ['É necessário selecionar um funil.'] });
      return;
    }

    if (shouldMoveCustomerToNextFunnel && !selectedNextFunnel) {
      setErrors({ next_funnel: ['É necessário selecionar um funil.'] });
      return;
    }

    setLoading(true);

    const params = {
      name: capitalizeFirstLetter(data.name as string),
      behaviour: selectedBehaviour,
      ask_negotiated_amount: askNegotiatedAmount,
      is_visible_in_a_specific_funnel: isVisibleInASpecificFunnel,
      visible_on_funnel_id: selectedVisibleOnFunnel?.id,
      should_move_customer_to_next_funnel: shouldMoveCustomerToNextFunnel,
      next_funnel_id: selectedNextFunnel?.id,
    };
    
    try {
      let response;

      if (isEditing) {
        response = await api.put(`/disposition_option/${option.id}`, {
          ...params,
          is_enabled: option.is_enabled,
        });
      } else {
        response = await api.post('/disposition_option', {
          ...params,
        });
      }
      
      if (response?.status === 200) {
        toast.success('Opção de tabulação salva com sucesso!');
        afterSubmit();
        handleClose();
      } else if (response?.data?.errors) {
        setErrors(response.data.errors);
      }
    } catch (error) {
      toast.error('Erro ao salvar opção de tabulação. Verifique os dados e tente novamente.');
      datadogRum.addError(new Error('Fail to save disposition option'), {
        error,
        optionId: option?.id,
        params,
      });
    } finally {
      setLoading(false);
    };
  };

  useEffect(() => {
    getSalesFunnels({});
  }, [getSalesFunnels]);

  return (
    <Dialog.Root size="md" placement="center" open onOpenChange={handleClose} closeOnInteractOutside={false}>
      <Portal>
        <CustomDialogBackdrop />
        <Dialog.Positioner>
          <Dialog.Content bg="grayscale.fg">
            <Dialog.Header>
              <Dialog.Title>
                {isEditing ? 'Editar opção de tabulação' : 'Nova opção de tabulação'}
              </Dialog.Title>
            </Dialog.Header>

            {(loadingBehaviours || behaviours.length < 1) ? <Loading /> : (
              <form onSubmit={onSubmit}>
                <Dialog.Body>
                  <Field.Root invalid={!!errors?.name}>
                    <Field.Label>Nome</Field.Label>
                    <InputGroup width="full" gap={0}>
                      <Input
                        name="name"
                        placeholder="Digite aqui..."
                        defaultValue={option?.name || ''}
                        minLength={3}
                        variant="subtle"
                        required
                      />
                    </InputGroup>
                    {errors?.name?.map((error, index) => (
                      <Field.ErrorText key={index}>{error}</Field.ErrorText>
                    ))}
                  </Field.Root>

                  <Box mt={4}>
                    <Text fontWeight={500} mb={1}>Comportamento</Text>
                    <NativeSelect.Root size="md" variant="subtle" invalid={!!errors?.behaviour}>
                      <NativeSelect.Field
                        placeholder="Selecione uma opção"
                        value={selectedBehaviour}
                        defaultValue={option?.behaviour}
                        onChange={(e) => setSelectedBehaviour(Number(e.currentTarget.value))}
                      >
                        {behaviours.map((behaviour) => (
                          <option key={behaviour.id} value={behaviour.id}>
                            {behaviour.name}
                          </option>
                        ))}
                      </NativeSelect.Field>
                      <NativeSelect.Indicator />
                    </NativeSelect.Root>
                    {errors?.behaviour?.map((error, index) => (
                      <ErrorLabel key={index} text={error} />
                    ))}
                  </Box>

                  <Box mt={4}>
                    <Checkbox.Root
                      checked={askNegotiatedAmount}
                      onCheckedChange={(e) => setAskNegotiatedAmount(e.checked as boolean)}
                      cursor="pointer"
                    >
                      <Checkbox.HiddenInput />
                      <Checkbox.Control colorPalette="argos" />
                      <Checkbox.Label fontWeight={400}>
                        Deve preencher valor negociado?
                      </Checkbox.Label>
                    </Checkbox.Root>
                    {errors?.ask_negotiated_amount?.map((error, index) => (
                      <ErrorLabel key={index} text={error} />
                    ))}
                  </Box>

                  <Box mt={4}>
                    <Checkbox.Root
                      checked={isVisibleInASpecificFunnel}
                      onCheckedChange={(e) => setIsVisibleInASpecificFunnel(e.checked as boolean)}
                      cursor="pointer"
                    >
                      <Checkbox.HiddenInput />
                      <Checkbox.Control colorPalette="argos" />
                      <Checkbox.Label fontWeight={400}>
                        Disponível em funil específico?
                      </Checkbox.Label>
                    </Checkbox.Root>
                    {errors?.is_visible_in_a_specific_funnel?.map((error, index) => (
                      <ErrorLabel key={index} text={error} />
                    ))}
                  </Box>

                  {isVisibleInASpecificFunnel && salesFunnels.length > 0 && (
                    <Box my={4}>
                      <SaleFunnelSelect
                        invalid={!!errors?.visible_on_funnel}
                        salesFunnel={salesFunnels}
                        onSearch={async (text) => await getSalesFunnels({ name: text })}
                        value={selectedVisibleOnFunnel}
                        onChange={(saleFunnel) => setSelectedVisibleOnFunnel(saleFunnel)}
                      />
                      {errors?.visible_on_funnel?.map((error, index) => (
                        <ErrorLabel key={index} text={error} />
                      ))}
                    </Box>
                  )}

                  <Box mt={4}>
                    <Checkbox.Root
                      checked={shouldMoveCustomerToNextFunnel}
                      onCheckedChange={(e) => setShouldMoveCustomerToNextFunnel(e.checked as boolean)}
                      cursor="pointer"
                    >
                      <Checkbox.HiddenInput />
                      <Checkbox.Control colorPalette="argos" />
                      <Checkbox.Label fontWeight={400}>
                        Ao tabular, deve mover o cliente para o próximo funil?
                      </Checkbox.Label>
                    </Checkbox.Root>
                    {errors?.should_move_customer_to_next_funnel?.map((error, index) => (
                      <ErrorLabel key={index} text={error} />
                    ))}
                  </Box>

                  {shouldMoveCustomerToNextFunnel && salesFunnels.length > 0 && (
                    <Box my={4}>
                      <SaleFunnelSelect
                        invalid={!!errors?.next_funnel}
                        salesFunnel={salesFunnels}
                        onSearch={async (text) => await getSalesFunnels({ name: text })}
                        value={selectedNextFunnel}
                        onChange={(saleFunnel) => setSelectedNextFunnel(saleFunnel)}
                      />
                      {errors?.next_funnel?.map((error, index) => (
                        <ErrorLabel key={index} text={error} />
                      ))}
                    </Box>
                  )}

                  {errors?.general && (
                    <Flex direction="column" my={4}>
                      {errors.general.map((error, index) => (
                        <ErrorLabel key={index} text={error} />
                      ))}
                    </Flex>
                  )}
                </Dialog.Body>

                <Dialog.Footer>
                  <Dialog.ActionTrigger asChild>
                    <Button variant="outline" disabled={loading}>
                      Cancelar
                    </Button>
                  </Dialog.ActionTrigger>
                  <Button bg="green.700" color="white" type="submit" loading={loading}>
                    Salvar
                  </Button>
                </Dialog.Footer>
              </form>
            )}
            <Dialog.CloseTrigger asChild>
              <CloseButton size="sm" />
            </Dialog.CloseTrigger>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};

export default UpsertDialog;
